import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Phone, Search, Plus, X, Trash2, ChevronUp, ChevronDown, Send, Users, Settings,
  Check, AlertCircle, User, Headphones, UserPlus, Building2, UsersRound, Lock,
  Star, Palette, Upload, Image as ImageIcon, History, RefreshCw, Network, Video, MapPin,
} from 'lucide-react';
import {
  api, openInTeams, MeResponse, EffectiveEntry, Branding, DirectoryUser, DirectoryQueue, DirectoryContact,
  AuditEntry, NumbersHidden, NumbersFriendlyNames, AudioAssetMeta,
} from '../api/client';
import { useApi } from '../hooks/useApi';
import { SLUG_RE, RESERVED_FIRST_SEGMENTS as RESERVED_SLUGS } from '../lib/webRoutes';
import { usePresence } from '../hooks/usePresence';
import { CallFlowsTab } from './CallFlowsTab';
import { CallFlowEditor, type AudioPickerRenderer } from './CallFlowEditor';
import { CallFlowImport } from './CallFlowImport';
import { AutoSyncDialog } from './AutoSyncDialog';
import { AudioPicker } from './callFlowEditor/AudioPicker';
import { NumbersByLocation } from './NumbersByLocation';
import { NumbersAccessAdmin } from './NumbersAccessAdmin';
import { NumbersAdmin } from './NumbersAdmin';
import type { CallFlow, CallFlowInput, ImportProposal, BrandingUpdate } from '../api/client';

// ---------- Types ----------

type ItemType = 'user' | 'queue' | 'contact' | 'personal';

interface DirectoryItem {
  id: string;
  type: ItemType;
  displayName: string;
  /** Optional short label shown instead of displayName (manual + personal entries). */
  friendlyName?: string;
  phoneNumber?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  email?: string;
  notes?: string;
  /** Numbers tab (admin only): whether this entry is hidden from the list. */
  hidden?: boolean;
  hiddenReason?: 'explicit' | 'rule';
  /** Numbers tab (admin only): location slug this entry groups under (undefined
   *  for shared queues / unmapped offices). */
  ownSlug?: string;
}

interface PickerProps {
  directory: DirectoryItem[];
  alreadyAdded: string[];
  targetName: string;
  targetKind: 'user' | 'group' | 'personal';
  addManualLabel: string;
  onAdd: (id: string) => Promise<void>;
  onAddManualContact: () => void;
  onClose: () => void;
}

interface ToastState { message: string; type: 'success' | 'error'; }

/** The User/Admin switch. Rendered in this app's own header inside Teams, and
 *  by the browser shell's top bar, which owns the state there. */
export function ViewToggle({ view, onChange, accentColor }: {
  view: 'user' | 'admin';
  onChange: (view: 'user' | 'admin') => void;
  accentColor: string;
}) {
  const cls = (active: boolean) =>
    `px-3 py-1.5 text-sm rounded transition-colors ${active ? 'bg-white shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`;
  return (
    <div className="flex bg-gray-100 rounded-md p-1 shrink-0">
      <button onClick={() => onChange('user')} className={cls(view === 'user')} style={view === 'user' ? { color: accentColor } : {}}>
        <Users className="w-4 h-4 inline mr-1.5" />User
      </button>
      <button onClick={() => onChange('admin')} className={cls(view === 'admin')} style={view === 'admin' ? { color: accentColor } : {}}>
        <Settings className="w-4 h-4 inline mr-1.5" />Admin
      </button>
    </div>
  );
}

interface TeamsQuickDialProps {
  me: MeResponse;
  initialView: 'user' | 'admin';
  /** Controlled view. Set by the browser shell, which renders the toggle in its
   *  own top bar; omitted inside Teams, where this app keeps the state. */
  view?: 'user' | 'admin';
  onViewChange?: (view: 'user' | 'admin') => void;
  /** The host already shows branding and the view toggle, so skip our header. */
  hideHeader?: boolean;
  theme: 'default' | 'dark' | 'contrast';
  /** Deep link from the Teams manifest (?tab=callflows) or a shared URL. */
  initialUserTab?: 'dial' | 'numbers' | 'callflows';
  initialLocationId?: string;
  /** Browser entry points (/<company>/home/app?admin=…&action=…). */
  initialAdminTab?: 'users' | 'groups' | 'numbers' | 'locations' | 'callflows' | 'branding' | 'audit';
  initialAction?: 'import' | 'sync';
  /** '' or '/<company>': prefix of the browser pages, for links out of the app. */
  webBasePath?: string;
}

// ---------- Constants & helpers ----------

const DEFAULT_BRANDING: Branding = {
  appName: 'Quick Dial',
  tagline: 'Group-based, per-user, and personal quick dial',
  accentColor: '#7c3aed',
  logoBlobUrl: null,
  includeQueues: true,
};

const ACCENT_PRESETS = [
  { name: 'Teams Purple', value: '#7c3aed' },
  { name: 'Royal Blue', value: '#2563eb' },
  { name: 'Teal', value: '#0d9488' },
  { name: 'Emerald', value: '#059669' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Rose', value: '#e11d48' },
  { name: 'Slate', value: '#475569' },
  { name: 'Indigo', value: '#4f46e5' },
];

function hexToRgb(hex: string) {
  const m = hex.replace('#', '').match(/.{2}/g);
  if (!m) return { r: 124, g: 58, b: 237 };
  return { r: parseInt(m[0], 16), g: parseInt(m[1], 16), b: parseInt(m[2], 16) };
}
function rgba(hex: string, a: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// Label shown in the call lists: a user-set friendly name wins over the official
// displayName, so long or look-alike names stay distinguishable.
function displayLabel(d: { friendlyName?: string; displayName: string }) {
  return (d.friendlyName && d.friendlyName.trim()) || d.displayName;
}

function formatRelative(iso?: string) {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

const typeColor = (t: ItemType) =>
  t === 'user' ? 'bg-purple-500' :
  t === 'queue' ? 'bg-teal-500' :
  t === 'personal' ? 'bg-pink-500' :
  'bg-blue-500';

// Initiate a call through Teams via the l/call deep link.
//  - A tenant USER → internal Teams audio call by UPN (free, richer).
//  - A call queue / auto-attendant WITH a direct number → dialled by its number
//    (PSTN form `users=4:<E.164>`), reachable by external and internal callers.
//  - A call queue WITHOUT a number → internal Teams call to its resource-account
//    UPN. Per Microsoft, internal Teams users reach a queue/auto-attendant by
//    calling its resource account; only EXTERNAL/PSTN callers need a direct
//    number (the number is optional on the resource account).
//  - Contacts / personal entries → their number.
function dial(item: { type: ItemType; email?: string; phoneNumber?: string }, method?: 'number' | 'teams') {
  // openInTeams hands the deep link to the Teams host (app.openLink) so the
  // call opens IN Teams — on mobile this means the in-app dialer, not a browser
  // page that then bounces into the app.
  const call = (target: string) =>
    openInTeams(`https://teams.microsoft.com/l/call/0/0?users=${target}`);
  const num = item.phoneNumber?.replace(/[\s()-]/g, '');
  const callNumber = () => { if (num) call(`4:${encodeURIComponent(num)}`); };
  const callTeams = () => { if (item.email) call(encodeURIComponent(item.email)); };

  if (method === 'number') return callNumber();
  if (method === 'teams') return callTeams();
  // Default (plain "Call"): a tenant user → internal Teams call; anything else
  // with a number (queues, contacts) → dial its number.
  if (item.type === 'user' && item.email) callTeams();
  else if (num) callNumber();
  else callTeams();
}

// Call affordance. When an entry has BOTH a number and a UPN (queues, most
// users) it's a split button: the main "Call" does the sensible default, and
// the caret lets you choose "Call the number" (PSTN/DID) vs "Call in Teams"
// (internal). With only one option it's a plain Call button.
function CallButton({ item, accent }: { item: { type: ItemType; email?: string; phoneNumber?: string }; accent: string }) {
  const [open, setOpen] = useState(false);
  const hasNumber = !!item.phoneNumber?.replace(/[\s()-]/g, '');
  // Queues show a single Call (no number-vs-Teams dropdown): a numbered queue
  // dials its DID; a numberless queue is reached by an internal Teams call to
  // its resource-account UPN — both handled by dial()'s default.
  const both = hasNumber && !!item.email && item.type !== 'queue';
  if (!both) {
    // Reserve the caret column (an invisible twin of the split button's caret) so
    // single-option Call buttons line up with the split ones across rows.
    return (
      <div className="flex shrink-0">
        <button onClick={() => dial(item)} className="px-3 py-1.5 text-white text-sm rounded font-medium flex items-center gap-1.5" style={{ backgroundColor: accent }}>
          <Phone className="w-3.5 h-3.5" />Call
        </button>
        <span aria-hidden="true" className="px-1.5 py-1.5 flex items-center border-l border-transparent invisible">
          <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </div>
    );
  }
  return (
    <div className="relative shrink-0">
      <div className="flex rounded overflow-hidden">
        <button onClick={() => dial(item)} className="px-3 py-1.5 text-white text-sm font-medium flex items-center gap-1.5" style={{ backgroundColor: accent }}>
          <Phone className="w-3.5 h-3.5" />Call
        </button>
        <button onClick={() => setOpen(o => !o)} aria-label="Call options" className="px-1.5 py-1.5 text-white flex items-center border-l border-white/30" style={{ backgroundColor: accent }}>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 w-44 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
            <button onClick={() => { dial(item, 'number'); setOpen(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-gray-500" />Call the number
            </button>
            <button onClick={() => { dial(item, 'teams'); setOpen(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100">
              <Video className="w-3.5 h-3.5 text-gray-500" />Call in Teams
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const typeLabel = (t: ItemType) =>
  t === 'user' ? 'User' :
  t === 'queue' ? 'Queue' :
  t === 'personal' ? 'Personal' :
  'Contact';

// Large tenants return thousands of users/groups now that the backend
// paginates Graph fully — cap rendered rows; search narrows the rest.
const MAX_LIST_ROWS = 100;

function MoreRowsHint({ total }: { total: number }) {
  if (total <= MAX_LIST_ROWS) return null;
  return (
    <div className="p-2.5 text-center text-xs text-gray-400 bg-gray-50">
      Showing {MAX_LIST_ROWS} of {total} — refine your search to narrow down
    </div>
  );
}

// ---------- Main component ----------

export function TeamsQuickDial({ me, initialView, view: controlledView, onViewChange, hideHeader, theme, initialUserTab, initialLocationId, initialAdminTab, initialAction, webBasePath }: TeamsQuickDialProps) {
  const [ownView, setOwnView] = useState<'user' | 'admin'>(initialView);
  const view = controlledView ?? ownView;
  const setView = onViewChange ?? setOwnView;
  const [adminTab, setAdminTab] = useState<'users' | 'groups' | 'numbers' | 'locations' | 'callflows' | 'branding' | 'audit'>(initialAdminTab || 'users');
  const [userTab, setUserTab] = useState<'dial' | 'numbers' | 'callflows'>(initialUserTab || 'dial');
  // null = closed; { flow: null } = create new; { flow } = edit existing.
  // allowDelete=false for import proposals that don't exist server-side yet.
  const [flowEditor, setFlowEditor] = useState<{ flow: CallFlow | null; allowDelete?: boolean } | null>(null);
  const [importOpen, setImportOpen] = useState(initialAction === 'import');
  const [syncOpen, setSyncOpen] = useState(initialAction === 'sync');
  const [importSaved, setImportSaved] = useState<Set<string>>(new Set());

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');

  const [picker, setPicker] = useState<{ kind: 'user' | 'group' | 'personal'; id: string } | null>(null);
  const [addContactCtx, setAddContactCtx] = useState<{ personal: boolean } | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // --- Polled & one-shot queries ---
  const { data: brandingRaw, refetch: refetchBranding } = useApi(() => api.getBranding(), { pollMs: 8000 });
  const { data: directoryRaw, refetch: refetchDirectory } = useApi(() => api.getDirectory(), { pollMs: 30000 });
  const { data: groups, refetch: refetchGroups } = useApi(() => api.listGroups(), { pollMs: 8000 });
  const { data: effective, refetch: refetchEffective } = useApi(() => api.getEffective('me'), { pollMs: 8000 });
  const { data: personal, refetch: refetchPersonal, setData: setPersonal } = useApi(() => api.getPersonal());
  // Group-filtered server-side; empty for users with no visible locations.
  // Polled so a transient first-load failure doesn't hide the tab all session.
  const { data: callFlows, refetch: refetchCallFlows } = useApi(() => api.listCallFlows(), { pollMs: 30000 });
  // Numbers tab data — location-authorized server-side (own office location +
  // group-mapped locations; admins see all). Distinct from the full directory.
  const { data: numbersData, error: numbersError, refetch: refetchNumbers } = useApi(() => api.getNumbers(), { pollMs: 30000 });
  // Admin-only: tenant-wide hidden config (rules + individually hidden entries).
  // Non-admins never fetch or receive it; their hidden entries are dropped server-side.
  const { data: hiddenConfig, refetch: refetchHidden, setData: setHiddenConfig } = useApi(
    () => me.isAdmin ? api.getNumbersHidden() : Promise.resolve(null),
    { deps: [me.isAdmin] }
  );
  // Mirror the latest hidden config in a ref so rapid hide/unhide clicks build on
  // each other's result (not a stale render snapshot) — avoids last-write-wins.
  const hiddenRef = useRef<NumbersHidden | null>(null);
  useEffect(() => { hiddenRef.current = hiddenConfig ?? null; }, [hiddenConfig]);
  // Admin-only: friendly-name overrides (tenant-wide + per-site). Same ref pattern
  // so rapid renames build on the freshest config, not a stale snapshot.
  const { data: friendlyConfig, refetch: refetchFriendly, setData: setFriendlyConfig } = useApi(
    () => me.isAdmin ? api.getNumbersFriendly() : Promise.resolve(null),
    { deps: [me.isAdmin] }
  );
  const friendlyRef = useRef<NumbersFriendlyNames | null>(null);
  useEffect(() => { friendlyRef.current = friendlyConfig ?? null; }, [friendlyConfig]);
  // Admin-only: the tenant's audio recordings (greetings, hold music) for the
  // call-flow editor's prompt pickers. Refreshed after every upload.
  const { data: audioAssets, refetch: refetchAudio } = useApi(
    () => me.isAdmin ? api.listCallFlowAudio() : Promise.resolve([] as AudioAssetMeta[]),
    { deps: [me.isAdmin] }
  );
  const audioPicker = useMemo<AudioPickerRenderer>(() => {
    const assets = audioAssets || [];
    const onUpload = async (file: File, meta: { teamsAudioFileId?: string; fileName?: string }) => {
      const asset = await api.uploadCallFlowAudio(file, meta);
      await refetchAudio();
      await refetchCallFlows();
      return asset;
    };
    return (value, onChange) => <AudioPicker value={value} onChange={onChange} assets={assets} onUpload={onUpload} />;
  }, [audioAssets, refetchAudio, refetchCallFlows]);

  async function saveCallFlow(locationId: string, body: CallFlowInput) {
    await api.putCallFlow(locationId, body);
    await refetchCallFlows();
    setFlowEditor(null);
    setImportSaved(prev => new Set(prev).add(locationId));
    showToast('Location saved');
  }

  async function removeCallFlow(locationId: string) {
    await api.deleteCallFlow(locationId);
    await refetchCallFlows();
    setFlowEditor(null);
    showToast('Location deleted');
  }

  const selectedUserFetcher = useCallback(
    () => (selectedUserId && me.isAdmin
      ? api.getEffective(selectedUserId)
      : Promise.resolve({ entries: [] as EffectiveEntry[], resolvedAt: '' })),
    [selectedUserId, me.isAdmin]
  );
  const { data: selectedUserEffective, refetch: refetchSelectedUserEffective } = useApi(
    selectedUserFetcher,
    { deps: [selectedUserId, me.isAdmin] }
  );

  const branding = brandingRaw || DEFAULT_BRANDING;

  // --- Default selections once data loads ---
  useEffect(() => {
    if (directoryRaw && !selectedUserId && directoryRaw.users.length > 0) {
      setSelectedUserId(directoryRaw.users[0].id);
    }
  }, [directoryRaw, selectedUserId]);

  useEffect(() => {
    if (groups && !selectedGroupId && groups.length > 0) {
      const first = groups.find(g => !g.orphaned) || groups[0];
      setSelectedGroupId(first.aadGroupId);
    }
  }, [groups, selectedGroupId]);

  // --- Derived directory ---
  // Always include queues here so already-assigned queue items still resolve in
  // the user/group views even if the admin has toggled `includeQueues` off.
  // Queue visibility in the *picker* is controlled separately in buildPickerProps.
  const directory: DirectoryItem[] = useMemo(() => {
    if (!directoryRaw) return [];
    const u: DirectoryItem[] = (directoryRaw.users as DirectoryUser[]).map(x => ({
      id: x.id, type: 'user', displayName: x.displayName, jobTitle: x.jobTitle,
      department: x.department, officeLocation: x.officeLocation,
      phoneNumber: x.phoneNumber, email: x.email,
    }));
    const q: DirectoryItem[] = (directoryRaw.queues as DirectoryQueue[]).map(x => ({
      id: x.id, type: 'queue', displayName: x.displayName, jobTitle: x.jobTitle,
      department: x.department, phoneNumber: x.phoneNumber, email: x.email,
    }));
    const c: DirectoryItem[] = (directoryRaw.contacts as DirectoryContact[]).map(x => ({
      id: x.id, type: 'contact', displayName: x.displayName, friendlyName: x.friendlyName,
      phoneNumber: x.phoneNumber, notes: x.notes,
    }));
    return [...u, ...q, ...c];
  }, [directoryRaw]);

  const dirById = useMemo<Record<string, DirectoryItem>>(
    () => Object.fromEntries(directory.map(d => [d.id, d])),
    [directory]
  );

  const tenantUsers = useMemo(() => directory.filter(d => d.type === 'user'), [directory]);

  // User-view tabs (Quick dial / Numbers / Call flows) are ALWAYS shown; each
  // surface renders its own empty/unauthorized state. numberPeople comes from
  // the location-authorized /numbers endpoint (own office location + group-
  // mapped locations; admins see all) — NOT the full directory.
  const numberPeople = useMemo<DirectoryItem[]>(
    () => (numbersData?.users || []).map(x => ({
      id: x.id, type: 'user' as const, displayName: x.displayName, jobTitle: x.jobTitle,
      department: x.department, officeLocation: x.officeLocation,
      phoneNumber: x.phoneNumber, email: x.email,
      hidden: x.hidden, hiddenReason: x.hiddenReason, ownSlug: x.ownSlug,
    })),
    [numbersData]
  );
  // Call queues / auto-attendants — shared dial endpoints shown in their own
  // section on the Numbers tab (not location-grouped; they have no office).
  const numberQueues = useMemo<DirectoryItem[]>(
    () => (numbersData?.queues || []).map(x => ({
      id: x.id, type: 'queue' as const, displayName: x.displayName, jobTitle: x.jobTitle,
      department: x.department, officeLocation: x.officeLocation,
      phoneNumber: x.phoneNumber, email: x.email,
      hidden: x.hidden, hiddenReason: x.hiddenReason, ownSlug: x.ownSlug,
    })),
    [numbersData]
  );
  const activeUserTab = userTab;
  // Live agent presence for the phone-menu guide; only polled while the Call
  // flows tab is showing and there is a location to show.
  const { presence, presenceState, consentUrl: presenceConsentUrl, setVisibleIds } =
    usePresence(activeUserTab === 'callflows' && (callFlows?.length || 0) > 0);

  // Distinct Entra office-location values across the directory — suggested to
  // admins when mapping a logical location to raw officeLocation strings.
  const officeLocationSuggestions = useMemo(
    () => [...new Set(
      directory.filter(d => d.type === 'user' && d.officeLocation).map(d => d.officeLocation!.trim()),
    )].sort((a, b) => a.localeCompare(b)),
    [directory]
  );
  // Call queues offered to admins for assigning to a location.
  const directoryQueues = useMemo(
    () => directory.filter(d => d.type === 'queue').map(d => ({ id: d.id, displayName: d.displayName })),
    [directory]
  );
  // Distinct Entra departments — suggested for the hide-by-department rule.
  const departmentSuggestions = useMemo(
    () => [...new Set(directory.filter(d => d.type === 'user' && d.department).map(d => d.department!.trim()))]
      .sort((a, b) => a.localeCompare(b)),
    [directory]
  );

  // --- Hidden list (Numbers tab) mutations ---
  async function saveHidden(next: NumbersHidden) {
    // Optimistic: update the ref + state so the UI and the next click see the
    // fresh config immediately; refetch reconciles with the server truth.
    hiddenRef.current = next;
    setHiddenConfig(next);
    try {
      await api.putNumbersHidden(next);
      await Promise.all([refetchHidden(), refetchNumbers()]);
      showToast('Hidden list updated');
    } catch (e: any) {
      await refetchHidden(); // roll the optimistic change back to server state
      showToast(e.message || 'Failed to update hidden list', 'error');
    }
  }
  function hideNumber(p: { id: string }) {
    const cur = hiddenRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    if (cur.hiddenIds.some(x => x.toLowerCase() === id)) return;
    saveHidden({ ...cur, hiddenIds: [...cur.hiddenIds, id] });
  }
  function unhideNumber(p: { id: string }) {
    const cur = hiddenRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    // "Unhide" always fully reveals: drop any explicit hide AND force-show
    // against rules, so one click works whether it was hidden explicitly, by a
    // rule, or both.
    saveHidden({
      ...cur,
      hiddenIds: cur.hiddenIds.filter(x => x.toLowerCase() !== id),
      exceptionIds: cur.exceptionIds.some(x => x.toLowerCase() === id) ? cur.exceptionIds : [...cur.exceptionIds, id],
    });
  }
  // Per-site hide/unhide: scopes the hide to viewers of one location slug, so the
  // same entry can be hidden at one site and shown at another. Empty site lists
  // are pruned so the config doesn't accumulate stale keys.
  function hideNumberForSite(slug: string, p: { id: string }) {
    const cur = hiddenRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    const map = { ...(cur.siteHiddenIds || {}) };
    const list = map[slug] || [];
    if (list.some(x => x.toLowerCase() === id)) return;
    map[slug] = [...list, id];
    saveHidden({ ...cur, siteHiddenIds: map });
  }
  function unhideNumberForSite(slug: string, p: { id: string }) {
    const cur = hiddenRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    // Nothing to do if it isn't hidden AT THIS SITE — e.g. a tenant-wide hide shows
    // greyed here but is unhidden under "All sites", not per-site. Avoids a no-op
    // save (and its misleading toast).
    if (!(cur.siteHiddenIds?.[slug] || []).some(x => x.toLowerCase() === id)) return;
    const map = { ...(cur.siteHiddenIds || {}) };
    const list = (map[slug] || []).filter(x => x.toLowerCase() !== id);
    if (list.length) map[slug] = list; else delete map[slug];
    saveHidden({ ...cur, siteHiddenIds: map });
  }

  // --- Friendly-name (Numbers tab) mutations ---
  async function saveFriendly(next: NumbersFriendlyNames) {
    friendlyRef.current = next;
    setFriendlyConfig(next);
    try {
      await api.putNumbersFriendly(next);
      await Promise.all([refetchFriendly(), refetchNumbers()]);
      showToast('Names updated');
    } catch (e: any) {
      await refetchFriendly(); // roll the optimistic change back to server state
      showToast(e.message || 'Failed to update names', 'error');
    }
  }
  // Tenant-wide ("All sites") rename. Passing null/empty clears the override.
  function renameNumber(p: { id: string }, name: string | null) {
    const cur = friendlyRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    const names = { ...(cur.names || {}) };
    const v = (name || '').trim();
    if (v) names[id] = v; else delete names[id];
    saveFriendly({ ...cur, names });
  }
  // Per-site rename: the name only applies to viewers of one location slug. Empty
  // site maps are pruned so the doc doesn't accumulate stale keys.
  function renameNumberForSite(slug: string, p: { id: string }, name: string | null) {
    const cur = friendlyRef.current;
    if (!cur) return;
    const id = p.id.toLowerCase();
    const siteNames = { ...(cur.siteNames || {}) };
    const map = { ...(siteNames[slug] || {}) };
    const v = (name || '').trim();
    if (v) map[id] = v; else delete map[id];
    if (Object.keys(map).length) siteNames[slug] = map; else delete siteNames[slug];
    saveFriendly({ ...cur, siteNames });
  }

  const personalResolver = useMemo<Record<string, DirectoryItem>>(() => {
    if (!personal) return dirById;
    const personalAsItems: DirectoryItem[] = personal.contacts.map(c => ({
      id: c.id, type: 'personal', displayName: c.displayName, friendlyName: c.friendlyName,
      phoneNumber: c.phoneNumber, notes: c.notes,
    }));
    return { ...dirById, ...Object.fromEntries(personalAsItems.map(d => [d.id, d])) };
  }, [dirById, personal]);

  // Personal entries shown to the user, deduped against admin-pushed
  const adminPushedIdSet = useMemo(
    () => new Set((effective?.entries || []).map(e => e.contactId)),
    [effective]
  );
  const personalIds = (personal?.quickDialIds || []).filter(id => !adminPushedIdSet.has(id));

  // --- Filters ---
  const filteredUsers = tenantUsers.filter(u => {
    if (!userSearch) return true;
    const s = userSearch.toLowerCase();
    return u.displayName.toLowerCase().includes(s) || (u.department || '').toLowerCase().includes(s);
  });

  const filteredGroups = (groups || []).filter(g =>
    !groupSearch || g.displayName.toLowerCase().includes(groupSearch.toLowerCase())
  );

  const selectedUser = selectedUserId ? dirById[selectedUserId] : null;
  const selectedGroup = groups?.find(g => g.aadGroupId === selectedGroupId) || null;

  // For the selected admin user: their direct (non-group) entries, in order
  const selectedUserDirectIds = useMemo(
    () => (selectedUserEffective?.entries || [])
      .filter(e => e.source.type === 'direct')
      .map(e => e.contactId),
    [selectedUserEffective]
  );

  // --- Toast ---
  function showToast(message: string, type: 'success' | 'error' = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  }

  // --- Personal mutations ---
  async function addPersonal(contactId: string) {
    const current = personal || { contacts: [], quickDialIds: [] };
    if (current.quickDialIds.includes(contactId)) return;
    const next = { ...current, quickDialIds: [...current.quickDialIds, contactId] };
    setPersonal(next);
    try { await api.putPersonal(next); }
    catch (e: any) { await refetchPersonal(); showToast(e.message || 'Failed to add', 'error'); }
  }

  async function removePersonal(contactId: string) {
    if (!personal) return;
    const next = { ...personal, quickDialIds: personal.quickDialIds.filter(id => id !== contactId) };
    setPersonal(next);
    try { await api.putPersonal(next); }
    catch (e: any) { await refetchPersonal(); showToast(e.message || 'Failed to remove', 'error'); }
  }

  async function reorderPersonalEntry(contactId: string, dir: 'up' | 'down') {
    if (!personal) return;
    const list = [...personal.quickDialIds];
    const idx = list.indexOf(contactId);
    if (idx === -1) return;
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= list.length) return;
    [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
    const next = { ...personal, quickDialIds: list };
    setPersonal(next);
    try { await api.putPersonal(next); }
    catch (e: any) { await refetchPersonal(); showToast(e.message || 'Failed to reorder', 'error'); }
  }

  async function addPersonalManual(data: { displayName: string; phoneNumber: string; notes?: string; friendlyName?: string }) {
    const current = personal || { contacts: [], quickDialIds: [] };
    const id = `p-${Date.now()}`;
    const next = {
      contacts: [...current.contacts, { id, ...data }],
      quickDialIds: [...current.quickDialIds, id],
    };
    setPersonal(next);
    try { await api.putPersonal(next); showToast('Personal contact added'); }
    catch (e: any) { await refetchPersonal(); showToast(e.message || 'Failed to add', 'error'); }
  }

  // --- Group mutations ---
  async function persistGroupAssignment(groupId: string, contactIds: string[]) {
    const g = groups?.find(x => x.aadGroupId === groupId);
    if (!g) return;
    try {
      await api.putGroupAssignment(groupId, { displayName: g.displayName, contactIds });
      await Promise.all([refetchGroups(), refetchEffective(), refetchSelectedUserEffective()]);
    } catch (e: any) { showToast(e.message || 'Failed', 'error'); }
  }
  async function addToGroup(groupId: string, contactId: string) {
    const g = groups?.find(x => x.aadGroupId === groupId);
    if (!g || g.contactIds.includes(contactId)) return;
    await persistGroupAssignment(groupId, [...g.contactIds, contactId]);
    showToast('Added to group');
  }
  async function removeFromGroup(groupId: string, contactId: string) {
    const g = groups?.find(x => x.aadGroupId === groupId);
    if (!g) return;
    await persistGroupAssignment(groupId, g.contactIds.filter(id => id !== contactId));
  }
  async function reorderInGroup(groupId: string, contactId: string, dir: 'up' | 'down') {
    const g = groups?.find(x => x.aadGroupId === groupId);
    if (!g) return;
    const list = [...g.contactIds];
    const idx = list.indexOf(contactId);
    if (idx === -1) return;
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= list.length) return;
    [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
    await persistGroupAssignment(groupId, list);
  }

  // --- User direct mutations ---
  async function persistUserAssignment(userId: string, contactIds: string[]) {
    try {
      await api.putUserAssignment(userId, contactIds);
      await refetchSelectedUserEffective();
      if (userId === me.userId) await refetchEffective();
    } catch (e: any) { showToast(e.message || 'Failed', 'error'); }
  }
  async function addToUser(userId: string, contactId: string) {
    if (selectedUserDirectIds.includes(contactId)) return;
    await persistUserAssignment(userId, [...selectedUserDirectIds, contactId]);
    showToast('Added direct entry');
  }
  async function removeFromUser(userId: string, contactId: string) {
    await persistUserAssignment(userId, selectedUserDirectIds.filter(id => id !== contactId));
  }
  async function reorderForUser(userId: string, contactId: string, dir: 'up' | 'down') {
    const list = [...selectedUserDirectIds];
    const idx = list.indexOf(contactId);
    if (idx === -1) return;
    const newIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= list.length) return;
    [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
    await persistUserAssignment(userId, list);
  }

  // --- Add manual tenant contact (admin) ---
  async function addTenantContact(data: { displayName: string; phoneNumber: string; notes?: string; friendlyName?: string }) {
    try {
      const c = await api.addContact(data);
      await refetchDirectory();
      // If a picker is open, immediately assign the new contact to its target
      if (picker?.kind === 'group') await addToGroup(picker.id, c.id);
      else if (picker?.kind === 'user') await addToUser(picker.id, c.id);
      else showToast('Contact added to directory');
    } catch (e: any) { showToast(e.message || 'Failed', 'error'); }
  }

  // --- Branding ---
  async function saveBranding(updates: BrandingUpdate) {
    try {
      await api.putBranding(updates);
      await refetchBranding();
      showToast('Branding saved');
    } catch (e: any) { showToast(e.message || 'Failed', 'error'); }
  }
  async function uploadLogoFile(file: File) {
    try {
      await api.uploadLogo(file);
      await refetchBranding();
      showToast('Logo uploaded');
    } catch (e: any) { showToast(e.message || 'Upload failed', 'error'); }
  }

  // --- Picker config ---
  // Hide queue items from the picker if the admin has disabled `includeQueues`.
  // We still keep them in `directory` (for resolving already-assigned ids),
  // we just don't offer new assignments to them.
  const pickerDirectory = useMemo(
    () => branding.includeQueues === false ? directory.filter(d => d.type !== 'queue') : directory,
    [directory, branding.includeQueues]
  );

  function buildPickerProps(): PickerProps | null {
    if (!picker) return null;
    if (picker.kind === 'personal') {
      const personalQuickDialIds = personal?.quickDialIds || [];
      const personalAsItems: DirectoryItem[] = (personal?.contacts || []).map(c => ({
        id: c.id, type: 'personal', displayName: c.displayName, friendlyName: c.friendlyName,
        phoneNumber: c.phoneNumber, notes: c.notes,
      }));
      return {
        directory: [...pickerDirectory, ...personalAsItems],
        alreadyAdded: [...adminPushedIdSet, ...personalQuickDialIds],
        targetName: 'your personal list',
        targetKind: 'personal',
        addManualLabel: 'Add new personal contact',
        onAdd: async (cid) => { await addPersonal(cid); },
        onAddManualContact: () => setAddContactCtx({ personal: true }),
        onClose: () => setPicker(null),
      };
    }
    if (picker.kind === 'user') {
      return {
        directory: pickerDirectory,
        alreadyAdded: selectedUserDirectIds,
        targetName: dirById[picker.id]?.displayName || 'user',
        targetKind: 'user',
        addManualLabel: 'Add new manual contact',
        onAdd: async (cid) => { await addToUser(picker.id, cid); },
        onAddManualContact: () => setAddContactCtx({ personal: false }),
        onClose: () => setPicker(null),
      };
    }
    const g = groups?.find(x => x.aadGroupId === picker.id);
    return {
      directory: pickerDirectory,
      alreadyAdded: g?.contactIds || [],
      targetName: g?.displayName || 'group',
      targetKind: 'group',
      addManualLabel: 'Add new manual contact',
      onAdd: async (cid) => { await addToGroup(picker.id, cid); },
      onAddManualContact: () => setAddContactCtx({ personal: false }),
      onClose: () => setPicker(null),
    };
  }
  const pickerProps = buildPickerProps();

  // --- Loading guard ---
  const initialLoading = !brandingRaw || !directoryRaw || !personal;
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading…</div>
      </div>
    );
  }

  // --- Style derived from theme ---
  const bgColor = theme === 'dark' ? '#1f1f1f' : theme === 'contrast' ? '#000000' : '#f9fafb';

  // The call-flow guide is for everyone to read, but only the Admin view may
  // change it — so the same panel is rendered in both places and `canEdit`
  // decides. The handlers are left undefined when read-only so a mis-set flag
  // still cannot open an editor.
  const callFlowsPanel = (canEdit: boolean) => (
    (callFlows?.length || 0) > 0 ? (
      <CallFlowsTab flows={callFlows!} accentColor={branding.accentColor} dirById={dirById} onDial={dial}
        isAdmin={canEdit}
        initialLocationId={initialLocationId}
        presence={presence} presenceState={presenceState} presenceConsentUrl={presenceConsentUrl} onVisibleAgents={setVisibleIds}
        onAddLocation={canEdit ? () => setFlowEditor({ flow: null }) : undefined}
        onEditLocation={canEdit ? (f => setFlowEditor({ flow: f })) : undefined}
        onImport={canEdit ? () => { setImportSaved(new Set()); setImportOpen(true); } : undefined}
        onSync={canEdit ? () => setSyncOpen(true) : undefined}
        menuBaseUrl={`${window.location.origin}${webBasePath || ''}/menu`} />
    ) : (
      <div className="bg-white rounded-lg border-2 border-dashed border-gray-200 p-10 text-center">
        <Network className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        {canEdit ? (
          <>
            <div className="text-sm text-gray-500 mb-1">No call flows documented yet</div>
            <div className="text-xs text-gray-400 mb-4">Document how incoming calls route at each of your locations.</div>
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => setFlowEditor({ flow: null })}
                className="px-4 py-2 text-sm text-white rounded font-medium"
                style={{ backgroundColor: branding.accentColor }}>
                <Plus className="w-4 h-4 inline mr-1.5" />Add your first location
              </button>
              <button onClick={() => { setImportSaved(new Set()); setImportOpen(true); }}
                className="px-4 py-2 text-sm border border-gray-300 rounded font-medium hover:bg-gray-50 text-gray-700">
                Import from Teams
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-gray-500 mb-1">No call flows shared with you</div>
            <div className="text-xs text-gray-400">When IT documents how calls route at your location, it'll appear here.</div>
          </>
        )}
      </div>
    )
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor, fontFamily: 'Segoe UI, system-ui, sans-serif' }}>
      {!hideHeader && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {branding.logoBlobUrl ? (
                <img src={branding.logoBlobUrl} alt="logo" className="w-9 h-9 rounded object-contain bg-white" />
              ) : (
                <div className="w-9 h-9 rounded flex items-center justify-center" style={{ backgroundColor: branding.accentColor }}>
                  <Phone className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-base font-semibold text-gray-900">{branding.appName}</h1>
                <p className="text-xs text-gray-500">{branding.tagline}</p>
              </div>
            </div>
            {me.isAdmin && <ViewToggle view={view} onChange={setView} accentColor={branding.accentColor} />}
          </div>
        </div>
      )}

      {view === 'admin' && me.isAdmin && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <div className="flex border-b border-gray-200">
            <button onClick={() => setAdminTab('users')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'users' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'users' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <User className="w-4 h-4 inline mr-1.5" />Users
            </button>
            <button onClick={() => setAdminTab('groups')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'groups' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'groups' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <UsersRound className="w-4 h-4 inline mr-1.5" />Groups
              <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{groups?.length || 0}</span>
            </button>
            <button onClick={() => setAdminTab('numbers')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'numbers' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'numbers' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Building2 className="w-4 h-4 inline mr-1.5" />Numbers
            </button>
            <button onClick={() => setAdminTab('locations')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'locations' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'locations' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <MapPin className="w-4 h-4 inline mr-1.5" />Locations
            </button>
            <button onClick={() => setAdminTab('callflows')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'callflows' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'callflows' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Network className="w-4 h-4 inline mr-1.5" />Call flows
            </button>
            <button onClick={() => setAdminTab('branding')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'branding' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'branding' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Palette className="w-4 h-4 inline mr-1.5" />Branding
            </button>
            <button onClick={() => setAdminTab('audit')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${adminTab === 'audit' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={adminTab === 'audit' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <History className="w-4 h-4 inline mr-1.5" />Audit
            </button>
          </div>
        </div>
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'callflows' && (
        <div className="max-w-6xl mx-auto px-6 py-5">
          {callFlowsPanel(true)}
        </div>
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'numbers' && (
        numbersData === null && !numbersError ? (
          <div className="max-w-3xl mx-auto px-6 py-4">
            <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-sm text-gray-500">Loading numbers…</div>
          </div>
        ) : numbersError && numberPeople.length === 0 && numberQueues.length === 0 ? (
          <div className="max-w-3xl mx-auto px-6 py-4">
            <div className="bg-white rounded-lg border-2 border-dashed border-red-200 p-10 text-center">
              <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
              <div className="text-sm text-gray-700 mb-1">Couldn't load numbers</div>
              <div className="text-xs text-gray-400 mb-4">{numbersError.message}</div>
              <button onClick={() => refetchNumbers()}
                className="px-4 py-2 text-sm border border-gray-300 rounded font-medium hover:bg-gray-50 text-gray-700">Retry</button>
            </div>
          </div>
        ) : (
          <NumbersAdmin
            people={numberPeople}
            queues={numberQueues}
            siteHiddenIds={hiddenConfig?.siteHiddenIds || {}}
            tenantNames={friendlyConfig?.names || {}}
            siteNames={friendlyConfig?.siteNames || {}}
            accentColor={branding.accentColor}
            onDial={(p) => { const item = dirById[p.id]; if (item) dial(item); }}
            renderCall={(p) => { const item = dirById[p.id]; return item ? <CallButton item={item} accent={branding.accentColor} /> : null; }}
            onHideAll={hideNumber}
            onUnhideAll={unhideNumber}
            onHideSite={hideNumberForSite}
            onUnhideSite={unhideNumberForSite}
            onRenameAll={renameNumber}
            onRenameSite={renameNumberForSite}
          />
        )
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'locations' && (
        <NumbersAccessAdmin
          groups={groups || []}
          queues={directoryQueues}
          officeLocationSuggestions={officeLocationSuggestions}
          departmentSuggestions={departmentSuggestions}
          hidden={hiddenConfig ?? null}
          onSaveHidden={saveHidden}
          accentColor={branding.accentColor}
          onToast={showToast}
        />
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'branding' && (
        <BrandingPanel branding={branding} onSave={saveBranding} onUploadLogo={uploadLogoFile} />
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'audit' && (
        <AuditPanel dirById={dirById} />
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'users' && (
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden grid md:grid-cols-[280px_1fr]">
            <div className="border-r border-gray-200 bg-gray-50/50">
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tenant users</div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..."
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
                </div>
              </div>
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                {filteredUsers.slice(0, MAX_LIST_ROWS).map(u => {
                  const isSelected = u.id === selectedUserId;
                  return (
                    <button key={u.id} onClick={() => setSelectedUserId(u.id)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-center gap-2.5 transition-colors ${isSelected ? 'bg-purple-50 border-l-2 border-l-purple-600' : 'hover:bg-white'}`}>
                      <div className={`w-8 h-8 rounded-full ${typeColor('user')} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                        {getInitials(u.displayName)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{u.displayName}</div>
                        <div className="text-xs text-gray-500 truncate">{u.department || ''}</div>
                      </div>
                    </button>
                  );
                })}
                <MoreRowsHint total={filteredUsers.length} />
              </div>
            </div>

            <div className="p-5">
              {selectedUser ? (
                <>
                  <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full ${typeColor('user')} flex items-center justify-center text-white text-base font-semibold`}>
                        {getInitials(selectedUser.displayName)}
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">{selectedUser.displayName}</h2>
                        <p className="text-sm text-gray-500">
                          {selectedUser.jobTitle}{selectedUser.jobTitle && selectedUser.department ? ' • ' : ''}{selectedUser.department}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-2 flex items-center justify-between mt-4">
                    <div className="text-sm font-medium text-gray-700">
                      IT-managed quick dial ({selectedUserEffective?.entries.length || 0})
                    </div>
                  </div>

                  {(selectedUserEffective?.entries.length || 0) === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center mb-3">
                      <Phone className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
                      <div className="text-sm text-gray-500">No quick dial entries</div>
                      <div className="text-xs text-gray-400">Add via groups (recommended) or directly below</div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
                      {(selectedUserEffective?.entries || []).map((entry, idx) => {
                        const c = dirById[entry.contactId];
                        const isDirect = entry.source.type === 'direct';
                        const directIdx = isDirect ? selectedUserDirectIds.indexOf(entry.contactId) : -1;
                        return (
                          <div key={entry.contactId} className="px-3 py-2.5 border-b border-gray-100 last:border-b-0 flex items-center gap-3 group hover:bg-gray-50">
                            <div className="text-xs text-gray-400 w-5 text-center font-medium">{idx + 1}</div>
                            <div className={`w-9 h-9 rounded-full ${typeColor(c?.type || 'contact')} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                              {c ? getInitials(c.displayName) : '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-sm font-medium text-gray-900 truncate">{c?.displayName || entry.contactId}</span>
                                {isDirect ? (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase tracking-wide font-medium">Direct</span>
                                ) : (
                                  <button onClick={() => { setAdminTab('groups'); setSelectedGroupId((entry.source as any).id); }}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 uppercase tracking-wide font-medium flex items-center gap-1">
                                    <UsersRound className="w-2.5 h-2.5" />via {(entry.source as any).name}
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">{c?.phoneNumber}</div>
                            </div>
                            {isDirect ? (
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => reorderForUser(selectedUser.id, entry.contactId, 'up')} disabled={directIdx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button onClick={() => reorderForUser(selectedUser.id, entry.contactId, 'down')} disabled={directIdx === selectedUserDirectIds.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                                <button onClick={() => removeFromUser(selectedUser.id, entry.contactId)} className="p-1 text-gray-400 hover:text-red-600 ml-1">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <Lock className="w-3.5 h-3.5 text-gray-300" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button onClick={() => setPicker({ kind: 'user', id: selectedUser.id })}
                    className="w-full px-3 py-2.5 border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5">
                    <Plus className="w-4 h-4" />Add direct entry for {selectedUser.displayName.split(' ')[0]}
                  </button>

                  <div className="mt-3 text-xs text-gray-500 flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Group entries can only be changed in the Groups tab. Users can also add their own personal entries — those are private and not shown here.</span>
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-400 text-center py-12">Select a user from the list.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'admin' && me.isAdmin && adminTab === 'groups' && (
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden grid md:grid-cols-[280px_1fr]">
            <div className="border-r border-gray-200 bg-gray-50/50">
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Security groups</div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input type="text" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Search groups..."
                    className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
                </div>
              </div>
              <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                {filteredGroups.slice(0, MAX_LIST_ROWS).map(g => {
                  const entryCount = g.contactIds.length;
                  const isSelected = g.aadGroupId === selectedGroupId;
                  return (
                    <button key={g.aadGroupId} onClick={() => setSelectedGroupId(g.aadGroupId)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 flex items-center gap-2.5 transition-colors ${isSelected ? 'bg-purple-50 border-l-2 border-l-purple-600' : 'hover:bg-white'}`}>
                      <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center text-gray-600 shrink-0">
                        <UsersRound className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                          {g.displayName}
                          {g.orphaned && <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">orphan</span>}
                        </div>
                        <div className="text-xs text-gray-500">{entryCount} entr{entryCount === 1 ? 'y' : 'ies'}</div>
                      </div>
                    </button>
                  );
                })}
                <MoreRowsHint total={filteredGroups.length} />
                {filteredGroups.length === 0 && (
                  <div className="p-6 text-center text-xs text-gray-400">No security groups found.</div>
                )}
              </div>
            </div>

            <div className="p-5">
              {selectedGroup ? (
                <>
                  <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center text-gray-600">
                        <UsersRound className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">{selectedGroup.displayName}</h2>
                        <p className="text-sm text-gray-500">{selectedGroup.description || '—'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 px-3 py-2 bg-gray-50 border border-gray-100 rounded text-xs text-gray-600 flex items-start gap-1.5">
                    <Building2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                    <span>Group membership is managed in Microsoft Entra. Quick dial entries here apply to whoever is currently a member of this group.</span>
                  </div>

                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Quick dial entries ({selectedGroup.contactIds.length})</div>
                  </div>

                  {selectedGroup.contactIds.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center mb-3">
                      <Phone className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
                      <div className="text-sm text-gray-500">No entries in this group's quick dial</div>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
                      {selectedGroup.contactIds.map((cid, idx) => {
                        const c = dirById[cid];
                        return (
                          <div key={cid} className="px-3 py-2.5 border-b border-gray-100 last:border-b-0 flex items-center gap-3 group hover:bg-gray-50">
                            <div className="text-xs text-gray-400 w-5 text-center font-medium">{idx + 1}</div>
                            <div className={`w-9 h-9 rounded-full ${typeColor(c?.type || 'contact')} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                              {c ? getInitials(c.displayName) : '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-gray-900 truncate">{c?.displayName || cid}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide font-medium shrink-0">
                                  {c ? typeLabel(c.type) : 'Unknown'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">{c?.phoneNumber}</div>
                            </div>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => reorderInGroup(selectedGroup.aadGroupId, cid, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button onClick={() => reorderInGroup(selectedGroup.aadGroupId, cid, 'down')} disabled={idx === selectedGroup.contactIds.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                                <ChevronDown className="w-4 h-4" />
                              </button>
                              <button onClick={() => removeFromGroup(selectedGroup.aadGroupId, cid)} className="p-1 text-gray-400 hover:text-red-600 ml-1">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <button onClick={() => setPicker({ kind: 'group', id: selectedGroup.aadGroupId })}
                    className="w-full px-3 py-2.5 border-2 border-dashed border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5">
                    <Plus className="w-4 h-4" />Add to {selectedGroup.displayName}
                  </button>

                  <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-green-600" />
                    Saved {formatRelative(selectedGroup.updatedAt)} — applies to all current members
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-400 text-center py-12">Select a group from the list.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {view === 'user' && (
        <div className={`${activeUserTab === 'callflows' ? 'max-w-6xl' : activeUserTab === 'numbers' ? 'max-w-3xl' : 'max-w-2xl'} mx-auto px-6 py-5`}>
          <div className="flex border-b border-gray-200 mb-4">
            <button onClick={() => setUserTab('dial')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${activeUserTab === 'dial' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={activeUserTab === 'dial' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Phone className="w-4 h-4 inline mr-1.5" />Quick dial
            </button>
            <button onClick={() => setUserTab('numbers')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${activeUserTab === 'numbers' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={activeUserTab === 'numbers' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Building2 className="w-4 h-4 inline mr-1.5" />Numbers
            </button>
            <button onClick={() => setUserTab('callflows')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${activeUserTab === 'callflows' ? '' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
              style={activeUserTab === 'callflows' ? { borderColor: branding.accentColor, color: branding.accentColor } : {}}>
              <Network className="w-4 h-4 inline mr-1.5" />Call flows
            </button>
          </div>
          {activeUserTab === 'callflows' ? (
            // Read-only here even for admins: the User view is what a user sees.
            // Editing lives in Admin → Call flows.
            callFlowsPanel(false)
          ) : activeUserTab === 'numbers' ? (
            numbersData === null && !numbersError ? (
              <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-sm text-gray-500">Loading numbers…</div>
            ) : numbersError && numberPeople.length === 0 && numberQueues.length === 0 ? (
              <div className="bg-white rounded-lg border-2 border-dashed border-red-200 p-10 text-center">
                <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
                <div className="text-sm text-gray-700 mb-1">Couldn't load numbers</div>
                <div className="text-xs text-gray-400 mb-4">{numbersError.message}</div>
                <button onClick={() => refetchNumbers()}
                  className="px-4 py-2 text-sm border border-gray-300 rounded font-medium hover:bg-gray-50 text-gray-700">Retry</button>
              </div>
            ) : numberPeople.length === 0 && numberQueues.length === 0 ? (
              <div className="bg-white rounded-lg border-2 border-dashed border-gray-200 p-10 text-center">
                <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <div className="text-sm text-gray-500 mb-1">No people available for your location</div>
                <div className="text-xs text-gray-400">
                  {me.isAdmin
                    ? 'No users have an office location set yet.'
                    : 'You can see people in your own office location and any locations IT has shared with your groups.'}
                </div>
              </div>
            ) : (
              <NumbersByLocation
                people={numberPeople}
                queues={numberQueues}
                isAdmin={me.isAdmin}
                onHide={hideNumber}
                onUnhide={unhideNumber}
                onDial={(p) => { const item = dirById[p.id]; if (item) dial(item); }}
                renderCall={(p) => {
                  const item = dirById[p.id];
                  if (!item) return null;
                  // Every user/queue has a UPN, so all are callable: a queue with no
                  // PSTN number is reached by an INTERNAL Teams call to its resource
                  // account (dial() routes no-number entries to the UPN form). Only
                  // external/PSTN callers need a direct number.
                  return <CallButton item={item} accent={branding.accentColor} />;
                }}
              />
            )
          ) : (
          <>
          {/* From IT section */}
          {effective && effective.entries.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-3">
              <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-400" />From IT
                </div>
                <div className="text-xs text-gray-500">{effective.entries.length}</div>
              </div>
              <div className="divide-y divide-gray-100">
                {effective.entries.map(entry => {
                  const c = dirById[entry.contactId];
                  if (!c) return null;
                  return (
                    <div key={entry.contactId} className="w-full px-4 py-3 hover:bg-purple-50/40 flex items-center gap-3">
                      <button onClick={() => dial(c)} className="flex-1 flex items-center gap-3 text-left min-w-0">
                        <div className={`w-11 h-11 rounded-full ${typeColor(c.type)} flex items-center justify-center text-white text-sm font-semibold shrink-0`}>
                          {getInitials(displayLabel(c))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 break-words sm:truncate" title={c.displayName}>{displayLabel(c)}</div>
                          <div className="text-xs text-gray-500">
                            {c.phoneNumber}{c.jobTitle && ` • ${c.jobTitle}`}{c.notes && ` • ${c.notes}`}
                          </div>
                        </div>
                      </button>
                      <CallButton item={c} accent={branding.accentColor} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Personal section */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 text-pink-500" />Personal
              </div>
              <div className="text-xs text-gray-500">{personalIds.length}</div>
            </div>
            {personalIds.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <div className="text-sm text-gray-500 mb-0.5">No personal entries yet</div>
                <div className="text-xs text-gray-400">Add contacts you call often — only you can see these.</div>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {personalIds.map((cid, idx) => {
                  const c = personalResolver[cid];
                  if (!c) return null;
                  return (
                    <div key={cid} className="px-4 py-3 hover:bg-purple-50/40 flex items-center gap-3 group">
                      <button onClick={() => dial(c)} className="flex-1 flex items-center gap-3 text-left min-w-0">
                        <div className={`w-11 h-11 rounded-full ${typeColor(c.type)} flex items-center justify-center text-white text-sm font-semibold shrink-0`}>
                          {getInitials(displayLabel(c))}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium text-gray-900 break-words sm:truncate" title={c.displayName}>{displayLabel(c)}</span>
                            {c.type === 'personal' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-100 text-pink-700 uppercase tracking-wide font-medium">Private</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {c.phoneNumber}{c.jobTitle && ` • ${c.jobTitle}`}{c.notes && ` • ${c.notes}`}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => reorderPersonalEntry(cid, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => reorderPersonalEntry(cid, 'down')} disabled={idx === personalIds.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => removePersonal(cid)} className="p-1 text-gray-400 hover:text-red-600 ml-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <CallButton item={c} accent={branding.accentColor} />
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={() => setPicker({ kind: 'personal', id: me.userId })}
              className="w-full px-3 py-2.5 border-t border-gray-200 text-purple-700 hover:bg-purple-50 text-sm font-medium flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" />Add personal entry
            </button>
          </div>

          <div className="mt-4 text-xs text-gray-400 text-center">IT manages the top list. Personal entries stay private to you.</div>
          </>
          )}
        </div>
      )}

      {importOpen && me.isAdmin && (
        <CallFlowImport
          savedIds={importSaved}
          accentColor={branding.accentColor}
          onReview={(p: ImportProposal) => setFlowEditor({ flow: p.draft, allowDelete: p.existing })}
          onAudioUploaded={async () => { await Promise.all([refetchAudio(), refetchCallFlows()]); }}
          onClose={() => setImportOpen(false)}
        />
      )}

      {syncOpen && me.isAdmin && (
        <AutoSyncDialog
          accentColor={branding.accentColor}
          appName={branding.appName}
          onReview={(p: ImportProposal) => { setSyncOpen(false); setFlowEditor({ flow: p.draft, allowDelete: p.existing }); }}
          onChanged={refetchCallFlows}
          onClose={() => setSyncOpen(false)}
        />
      )}

      {flowEditor && me.isAdmin && (
        <CallFlowEditor
          initial={flowEditor.flow}
          allowDelete={flowEditor.allowDelete}
          groups={groups || []}
          queues={directory.filter(d => d.type === 'queue').map(d => ({ id: d.id, displayName: d.displayName }))}
          directory={directory.filter(d => d.type === 'user').map(d => ({ id: d.id, displayName: d.displayName, email: d.email, jobTitle: d.jobTitle }))}
          accentColor={branding.accentColor}
          audioPicker={audioPicker}
          onSave={saveCallFlow}
          onDelete={removeCallFlow}
          onCancel={() => setFlowEditor(null)}
        />
      )}

      {pickerProps && <DirectoryPicker {...pickerProps} />}

      {addContactCtx && (
        <AddContactModal
          personal={addContactCtx.personal}
          onAdd={async (data) => {
            if (addContactCtx.personal) {
              await addPersonalManual(data);
            } else {
              await addTenantContact(data);
            }
            setAddContactCtx(null);
          }}
          onCancel={() => setAddContactCtx(null)}
        />
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm text-white z-50 ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </div>
  );
}

// ---------- DirectoryPicker ----------

function DirectoryPicker({
  directory, alreadyAdded, targetName, targetKind, onAdd, onAddManualContact,
  addManualLabel, onClose,
}: PickerProps) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | ItemType>('all');

  const filtered = useMemo(() => {
    let r = directory;
    if (typeFilter !== 'all') r = r.filter(d => d.type === typeFilter);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(d =>
        d.displayName.toLowerCase().includes(s) ||
        (d.friendlyName || '').toLowerCase().includes(s) ||
        (d.phoneNumber || '').includes(s) ||
        (d.department || '').toLowerCase().includes(s) ||
        (d.jobTitle || '').toLowerCase().includes(s) ||
        (d.notes || '').toLowerCase().includes(s)
      );
    }
    return [...r].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [directory, search, typeFilter]);

  const counts = {
    all: directory.length,
    user: directory.filter(d => d.type === 'user').length,
    queue: directory.filter(d => d.type === 'queue').length,
    contact: directory.filter(d => d.type === 'contact').length,
    personal: directory.filter(d => d.type === 'personal').length,
  };

  const chips: Array<{ id: 'all' | ItemType; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'user', label: 'Users' },
  ];
  if (counts.queue > 0) chips.push({ id: 'queue', label: 'Queues' });
  chips.push({ id: 'contact', label: 'Contacts' });
  if (counts.personal > 0) chips.push({ id: 'personal', label: 'Personal' });

  const targetIntro =
    targetKind === 'user' ? 'for user' :
    targetKind === 'group' ? 'for group' :
    'for';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-semibold">Add to quick dial</h2>
              <p className="text-xs text-gray-500">{targetIntro} <span className="font-medium">{targetName}</span></p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search directory..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(f => (
              <button key={f.id} onClick={() => setTypeFilter(f.id)}
                className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${typeFilter === f.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f.label} <span className="opacity-70">({counts[f.id]})</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No matches.</div>
          ) : filtered.slice(0, MAX_LIST_ROWS).map(d => {
            const added = alreadyAdded.includes(d.id);
            return (
              <button key={d.id} onClick={() => !added && onAdd(d.id)} disabled={added}
                className={`w-full px-4 py-2.5 border-b border-gray-100 flex items-center gap-3 text-left ${added ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}>
                <div className={`w-9 h-9 rounded-full ${typeColor(d.type)} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                  {d.type === 'queue' ? <Headphones className="w-4 h-4" /> : getInitials(displayLabel(d))}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate" title={d.displayName}>{displayLabel(d)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide font-medium shrink-0">{typeLabel(d.type)}</span>
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {d.phoneNumber}
                    {d.jobTitle && ` • ${d.jobTitle}`}
                    {d.department && ` • ${d.department}`}
                    {d.notes && ` • ${d.notes}`}
                  </div>
                </div>
                {added ? (
                  <span className="text-xs text-gray-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" />Added</span>
                ) : (
                  <Plus className="w-4 h-4 text-purple-600" />
                )}
              </button>
            );
          })}
          {filtered.length > 0 && <MoreRowsHint total={filtered.length} />}
        </div>
        <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button onClick={onAddManualContact} className="text-sm text-purple-700 hover:text-purple-900 flex items-center gap-1.5 font-medium">
            <UserPlus className="w-4 h-4" />{addManualLabel}
          </button>
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-white">Done</button>
        </div>
      </div>
    </div>
  );
}

// ---------- AddContactModal ----------

interface AddContactModalProps {
  personal: boolean;
  onAdd: (data: { displayName: string; phoneNumber: string; notes?: string; friendlyName?: string }) => Promise<void>;
  onCancel: () => void;
}

function AddContactModal({ personal, onAdd, onCancel }: AddContactModalProps) {
  const [displayName, setDisplayName] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">{personal ? 'Add personal contact' : 'Add manual contact'}</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-gray-600 mb-4">
          {personal
            ? 'Only visible in your personal quick dial. Not shared with IT or other users.'
            : 'External contact (vendor, support line) added to the shared tenant directory.'}
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Display name *</label>
            <input type="text" autoFocus value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder={personal ? 'e.g. Family doctor' : 'e.g. Acme Support'}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Friendly name</label>
            <input type="text" value={friendlyName} onChange={e => setFriendlyName(e.target.value)}
              placeholder="Optional short label, e.g. Bocholt — Sales"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
            <p className="text-[11px] text-gray-400 mt-1">Shown instead of the full name in your list — handy for long or look-alike names.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone number *</label>
            <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+31201234567"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button
            onClick={async () => {
              setSubmitting(true);
              await onAdd({ displayName, phoneNumber: phone, notes: notes || undefined, friendlyName: friendlyName || undefined });
              setSubmitting(false);
            }}
            disabled={!displayName || !phone || submitting}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded font-medium"
          >
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- BrandingPanel ----------

interface BrandingPanelProps {
  branding: Branding;
  onSave: (updates: BrandingUpdate) => Promise<void>;
  onUploadLogo: (file: File) => Promise<void>;
}

function BrandingPanel({ branding, onSave, onUploadLogo }: BrandingPanelProps) {
  const [draft, setDraft] = useState(branding);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Resync draft to branding from props ONLY when the user has no pending
  // edits. Without this guard, the parent's 8s polling overwrites whatever
  // the user is typing into the form mid-edit. Track the last-seen branding
  // via a ref so we accept incoming updates only when draft still matches it
  // (i.e. the user hasn't started typing).
  const lastSeenBrandingRef = useRef(branding);
  useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(lastSeenBrandingRef.current)) {
      setDraft(branding);
    }
    lastSeenBrandingRef.current = branding;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(branding);

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file (PNG, JPG, or SVG).');
      return;
    }
    if (file.size > 500_000) {
      setUploadError('Logo must be smaller than 500 KB.');
      return;
    }
    setUploading(true);
    try {
      await onUploadLogo(file);
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-4">
      <div className="grid md:grid-cols-2 gap-4">
        {/* Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Branding</h2>
          <p className="text-xs text-gray-500 mb-4">Customise how the app appears for everyone in the tenant.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">App name</label>
              <input type="text" value={draft.appName} onChange={e => setDraft({ ...draft, appName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
              <p className="text-[11px] text-gray-400 mt-1">Shown in the header and tab title.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tagline</label>
              <input type="text" value={draft.tagline} onChange={e => setDraft({ ...draft, tagline: e.target.value })}
                placeholder="Optional subtitle under the app name"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Web address</label>
              <div className="flex items-center">
                <span className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border border-r-0 border-gray-200 rounded-l whitespace-nowrap">{window.location.host}/</span>
                <input type="text" value={draft.slug || ''} onChange={e => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="your-company" spellCheck={false} maxLength={40}
                  className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-r focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500" />
              </div>
              {draft.slug && !SLUG_RE.test(draft.slug) ? (
                <p className="text-[11px] text-red-600 mt-1">3–40 lowercase letters, digits or dashes; no dash at the start or end.</p>
              ) : draft.slug && RESERVED_SLUGS.has(draft.slug) ? (
                <p className="text-[11px] text-red-600 mt-1">That address is reserved.</p>
              ) : (
                <p className="text-[11px] text-gray-400 mt-1">Your organisation's own sign-in page for the browser version. The name, logo and colour above are shown on that public page. Leave empty to use only the Teams app.</p>
              )}
              {branding.slug && (
                <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                  <a href={`${window.location.origin}/${branding.slug}`} target="_blank" rel="noopener noreferrer" className="font-mono text-purple-700 hover:underline truncate">{window.location.origin}/{branding.slug}</a>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/${branding.slug}`).catch(() => undefined); }}
                    className="px-1.5 py-0.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 shrink-0">Copy link</button>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Logo</label>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded border border-gray-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {branding.logoBlobUrl ? (
                    <img src={branding.logoBlobUrl} alt="logo preview" className="w-full h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-gray-300" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50 cursor-pointer">
                    <Upload className="w-3.5 h-3.5" />
                    {uploading ? 'Uploading…' : (branding.logoBlobUrl ? 'Replace logo' : 'Upload logo')}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} className="hidden" />
                  </label>
                  <p className="text-[11px] text-gray-400 mt-1">PNG/SVG, square, under 500 KB. Falls back to a phone icon.</p>
                  {uploadError && <p className="text-[11px] text-red-600 mt-1">{uploadError}</p>}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Accent color</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {ACCENT_PRESETS.map(p => (
                  <button key={p.value} onClick={() => setDraft({ ...draft, accentColor: p.value })}
                    className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${draft.accentColor === p.value ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                    style={{ backgroundColor: p.value }}
                    title={p.name} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="color" value={draft.accentColor}
                  onChange={e => setDraft({ ...draft, accentColor: e.target.value })}
                  className="w-9 h-9 border border-gray-200 rounded cursor-pointer" />
                <input type="text" value={draft.accentColor}
                  onChange={e => setDraft({ ...draft, accentColor: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm font-mono border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="#7c3aed" />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Used for the logo background, call buttons, and active states.</p>
            </div>

            <div className="pt-3 border-t border-gray-100">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.includeQueues !== false}
                  onChange={e => setDraft({ ...draft, includeQueues: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded accent-purple-600"
                />
                <span className="text-xs text-gray-700 leading-relaxed">
                  <span className="block font-medium text-gray-900 mb-0.5">Include call queues in the picker</span>
                  Show Teams Phone resource accounts (call queues, auto-attendants) when adding contacts to a group, user, or your personal list. Already-assigned queues stay visible regardless.
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
            <button onClick={() => setDraft({ ...DEFAULT_BRANDING, logoBlobUrl: branding.logoBlobUrl })}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">
              Reset to defaults
            </button>
            <button onClick={() => setDraft(branding)} disabled={!dirty}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              Discard
            </button>
            <button
              onClick={() => onSave({
                appName: draft.appName,
                tagline: draft.tagline,
                accentColor: draft.accentColor,
                includeQueues: draft.includeQueues,
                slug: draft.slug?.trim() ? draft.slug.trim() : null,
              })}
              disabled={!dirty || !draft.appName || (!!draft.slug && (!SLUG_RE.test(draft.slug) || RESERVED_SLUGS.has(draft.slug)))}
              className="px-3 py-1.5 text-sm text-white rounded font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              style={{ backgroundColor: dirty && draft.appName ? draft.accentColor : '#9ca3af' }}
            >
              <Send className="w-3.5 h-3.5" />Save & apply for all users
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Preview</div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
              {branding.logoBlobUrl ? (
                <img src={branding.logoBlobUrl} alt="logo" className="w-9 h-9 rounded object-contain" />
              ) : (
                <div className="w-9 h-9 rounded flex items-center justify-center" style={{ backgroundColor: draft.accentColor }}>
                  <Phone className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <div className="text-base font-semibold text-gray-900">{draft.appName || 'App name'}</div>
                {draft.tagline && <div className="text-xs text-gray-500">{draft.tagline}</div>}
              </div>
            </div>

            <div className="p-3 bg-gray-50/50 space-y-2">
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-xs" style={{ backgroundColor: draft.accentColor }}>
                  IH
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">IT Helpdesk</div>
                  <div className="text-xs text-gray-500">+31 20 123 4600</div>
                </div>
                <div className="px-3 py-1.5 text-white text-sm rounded font-medium flex items-center gap-1.5" style={{ backgroundColor: draft.accentColor }}>
                  <Phone className="w-3.5 h-3.5" />Call
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 px-3 py-2 rounded text-xs flex items-start gap-1.5" style={{ backgroundColor: rgba(draft.accentColor, 0.08), color: draft.accentColor }}>
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Changes apply tenant-wide on save. The header logo, call buttons, and active tab indicators will use this accent.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- AuditPanel ----------

interface AuditPanelProps {
  dirById: Record<string, DirectoryItem>;
}

const AUDIT_WINDOW_DAYS = 7;
const AUDIT_LIMIT = 100;

const AUDIT_ACTION_STYLES: Record<AuditEntry['action'], string> = {
  create: 'bg-green-50 text-green-700',
  update: 'bg-blue-50 text-blue-700',
  delete: 'bg-red-50 text-red-700',
};

// Initials from a display name or a UPN ("first.last@tenant.com" -> "FL").
// Tolerates audit docs written without userPrincipalName (the claim can be
// absent from some tokens and the backend stores it as-is).
function auditInitials(name?: string) {
  if (!name) return '?';
  return getInitials(name.split('@')[0].replace(/[._-]/g, ' ')) || '?';
}

// Fetches its own data so the request and 30s poll only run while the Audit
// tab is actually mounted (the render site gates on view/tab/isAdmin).
function AuditPanel({ dirById }: AuditPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, loading, error, refetch } = useApi(
    () => api.getAuditLog({
      since: new Date(Date.now() - AUDIT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      limit: AUDIT_LIMIT,
    }),
    { pollMs: 30000 }
  );
  const entries = data || [];

  return (
    <div className="max-w-6xl mx-auto px-6 py-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Audit log</h2>
            <p className="text-xs text-gray-500">
              Admin changes from the last {AUDIT_WINDOW_DAYS} days (latest {AUDIT_LIMIT} shown). Read-only.
            </p>
          </div>
          <button onClick={refetch}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" />Refresh
          </button>
        </div>

        {loading && entries.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading audit log…</div>
        ) : error && entries.length === 0 ? (
          <div className="m-5 border-2 border-dashed border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-7 h-7 text-red-300 mx-auto mb-1.5" />
            <div className="text-sm text-gray-700">Couldn't load the audit log</div>
            <div className="text-xs text-gray-400">{error.message} — use Refresh to retry</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="m-5 border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
            <History className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
            <div className="text-sm text-gray-500">No audit entries in the last {AUDIT_WINDOW_DAYS} days</div>
            <div className="text-xs text-gray-400">Changes made in the admin view will appear here</div>
          </div>
        ) : (
          entries.map(e => {
            const expanded = expandedId === e.id;
            const hasDetail = e.before != null || e.after != null;
            const actorName = dirById[e.userId]?.displayName || e.userPrincipalName || 'Unknown user';
            return (
              <div key={e.id} className="border-b border-gray-100 last:border-b-0">
                <button onClick={() => hasDetail && setExpandedId(expanded ? null : e.id)}
                  className={`w-full text-left px-5 py-2.5 flex items-center gap-3 ${hasDetail ? 'hover:bg-gray-50' : 'cursor-default'}`}>
                  <div className={`w-8 h-8 rounded-full ${typeColor('user')} flex items-center justify-center text-white text-xs font-semibold shrink-0`}>
                    {auditInitials(actorName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 truncate">
                      <span className="font-medium" title={e.userPrincipalName}>{actorName}</span>
                      <span className={`ml-2 px-1.5 py-0.5 rounded text-[11px] font-medium ${AUDIT_ACTION_STYLES[e.action] || 'bg-gray-100 text-gray-600'}`}>
                        {e.action}
                      </span>
                      <span className="ml-2 text-gray-600">{e.resource}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{e.target}</div>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0" title={new Date(e.timestamp).toLocaleString()}>
                    {formatRelative(e.timestamp)}
                  </div>
                  {hasDetail && (expanded
                    ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />)}
                </button>
                {expanded && (
                  <div className="px-5 pb-3 grid md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">Before</div>
                      <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto max-h-48">
                        {e.before != null ? JSON.stringify(e.before, null, 2) : '—'}
                      </pre>
                    </div>
                    <div>
                      <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">After</div>
                      <pre className="text-[11px] bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto max-h-48">
                        {e.after != null ? JSON.stringify(e.after, null, 2) : '—'}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
